import React from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./ProgramsBanner.css";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import "./SocialDevelopment.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

/* ---- Simple inline icons (no external icon-font dependency) ---- */
const IconLeaf = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M5 21c8 0 14-6 14-14V5h-2C9 5 3 11 3 19v2h2z" />
    <path d="M5 21c4-6 8-9 14-12" />
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const IconCoins = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <ellipse cx="9" cy="7" rx="6" ry="3" />
    <path d="M3 7v10c0 1.7 2.7 3 6 3s6-1.3 6-3V7" />
    <path d="M9 12c0 1.7 2.7 3 6 3s6-1.3 6-3-2.7-3-6-3" />
    <path d="M15 9v10c3.3 0 6-1.3 6-3V9" />
  </svg>
);

const IconBank = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M3 10l9-6 9 6" />
    <path d="M4 10h16v2H4z" />
    <path d="M5 12v7M9 12v7M15 12v7M19 12v7" />
    <path d="M3 21h18" />
  </svg>
);

const IconGroup = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <circle cx="9" cy="8" r="3" />
    <path d="M2.5 20c0-3.5 2.9-6 6.5-6s6.5 2.5 6.5 6" />
    <circle cx="17.5" cy="8.5" r="2.3" />
    <path d="M15.7 14c2.6.4 4.7 2.5 4.7 5.5" />
  </svg>
);

const IconMegaphone = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M3 10v4h3l6 4V6l-6 4H3z" />
    <path d="M14 9a4 4 0 010 6" />
    <path d="M17 6a8 8 0 010 12" />
  </svg>
);

const IconCalendarStar = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4M16 3v4" />
    <path d="M12 13l1 2 2 .3-1.5 1.4.4 2-1.9-1-1.9 1 .4-2L9 15.3l2-.3z" />
  </svg>
);

const IconTrendUp = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#1f9d55" strokeWidth="1.8">
    <path d="M3 17l6-6 4 4 8-8" />
    <path d="M15 7h6v6" />
  </svg>
);

const IconHeartFilled = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" stroke="none">
    <path d="M12 21s-7.5-4.6-10-9.3C.4 8.6 2 5 5.6 5c2 0 3.4 1.1 4.4 2.6C11 6.1 12.4 5 14.4 5 18 5 19.6 8.6 22 11.7 19.5 16.4 12 21 12 21z" />
  </svg>
);

const IconUserPlus = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2" />
    <path d="M18 8v5M20.5 10.5h-5" />
  </svg>
);

// Renamed component
const SocialDevelopment = () => {
  // Data for the initiatives section for cleaner code
  const programs = [
    {
      icon: <IconGroup />,
      title: "Community Meetings to Sensitize and Identify Issues",
      text: "Engaging communities to understand challenges and create solutions.",
    },
    {
      icon: <IconMegaphone />,
      title: "Empowering Disabled Leaders through Associations",
      text: "Building strong leadership for inclusive and empowered communities.",
    },
    {
      icon: <IconCalendarStar />,
      title: "Annual Disability Day Celebrations to Raise Awareness",
      text: "Creating awareness and promoting inclusion through events and activities.",
    },
  ];

  const highlights = [
    { icon: <IconShield />, text: "Social Security Support" },
    { icon: <IconCoins />, text: "Economic Empowerment" },
    { icon: <IconBank />, text: "Financial Inclusion" },
  ];

  const stats = [
    { icon: <IconGroup />, value: "500+", label: "Associations Formed" },
    { icon: <IconTrendUp />, value: "10K+", label: "Leaders Empowered" },
    { icon: <IconHeartFilled />, value: "50K+", label: "Lives Impacted" },
  ];

  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="page-header page-header--sd">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/page-header-bg.jpg)",
          }}
        ></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Social Development
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Vikash drives social development by providing training,
              resources, and opportunities to enhance livelihoods and promote
              sustainable growth.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* Introduction Section */}
      <section className="py-5 sd-commitment-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="align-items-center justify-content-center g-5">
              <motion.div className="col-lg-6" variants={staggerItem}>
                <span className="sd-eyebrow">
                  <IconLeaf /> Our Commitment
                </span>
                <h2 className="fw-bold mb-2" style={{ color: "#006633" }}>
                  Building a More Inclusive Society
                </h2>
                <div className="sd-heading-underline">
                  <IconLeaf />
                </div>
                <p className="lh-lg">
                  Many people with disabilities face social exclusion due to
                  poverty, discrimination, and various barriers. Vikash works
                  to include PWDs in government schemes, disseminate
                  information to empower them, and promote their social
                  inclusion. We connect them with programs for social
                  security, economic empowerment, and financial inclusion.
                </p>
              </motion.div>
              <motion.div className="col-lg-6" variants={staggerItem}>
                <div className="sd-image-card">
                  <img
                    src="/assets/images/Subsections/sde1.jpg"
                    alt="Community meeting for social inclusion"
                    className="img-fluid rounded-5 shadow"
                  />
                  <div className="sd-image-strip">
                    {highlights.map((h, idx) => (
                      <div className="sd-image-strip__item" key={idx}>
                        <span className="sd-image-strip__icon">{h.icon}</span>
                        <span>{h.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Leadership Section */}
      <section className="py-5 sd-leadership-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <div className="sd-leadership-box">
              <Row className="align-items-center g-5">
                <motion.div className="col-lg-6" variants={staggerItem}>
                  <span className="sd-eyebrow">
                    <IconLeaf /> Our Approach
                  </span>
                  <h2 className="fw-bold mb-2" style={{ color: "#006633" }}>
                    Fostering Leadership and Empowerment
                  </h2>
                  <div className="sd-heading-underline">
                    <IconLeaf />
                  </div>
                  <p className="lh-lg">
                    Vikash creates an environment to facilitate the emergence
                    of leadership from among persons with disability. We
                    facilitate the formation of Disabled People’s
                    Associations at State, District, and Block levels to
                    provide a unified voice for their empowerment.
                  </p>
                  <div className="sd-stats">
                    {stats.map((s, idx) => (
                      <div className="sd-stats__item" key={idx}>
                        <span className="sd-stats__icon">{s.icon}</span>
                        <span>
                          <strong>{s.value}</strong>
                          <br />
                          {s.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
                <motion.div className="col-lg-6" variants={staggerItem}>
                  <img
                    src="/assets/images/Subsections/sde2.jpg"
                    alt="Empowering disabled leaders"
                    className="img-fluid rounded-5 shadow"
                  />
                </motion.div>
              </Row>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Key Programs Section */}
      <section className="py-5 sd-programs-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center mb-5"
          >
            <span className="sd-eyebrow sd-eyebrow--center">
              <IconLeaf /> Our Key Programs
            </span>
            <h2 className="fw-bold" style={{ color: "#16341f" }}>
              Our Key Programs for Social Inclusion
            </h2>
            <p className="lead sd-subtext">
              We focus on community engagement and leadership to drive
              change.
            </p>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}
          >
            <Row className="g-4 justify-content-center">
              {programs.map((program, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div variants={staggerItem} className="h-100">
                    <Card className="text-center p-4 border-0 shadow-sm h-100 sd-program-card">
                      <div className="sd-program-icon">{program.icon}</div>
                      <h5>{program.title}</h5>
                      <p>{program.text}</p>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Call To Action Section */}
      <section className="py-5 sd-cta-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
          >
            <div className="sd-cta-box">
              <div className="sd-cta-content">
                <div className="sd-cta-icon">
                  <IconHeartFilled />
                </div>
                <span className="sd-eyebrow sd-eyebrow--hero">
                  Make A Difference
                </span>
                <h2 className="fw-bold mb-2 text-white">
                  Promote Social Inclusion
                </h2>
                <div className="sd-heading-underline sd-heading-underline--hero">
                  <IconLeaf />
                </div>
                <p className="mb-4">
                  Your contribution helps us empower communities and ensure
                  no one is left behind.
                </p>
                <div className="sd-cta-buttons">
                  <Button className="sd-btn sd-btn--solid" onClick={openDonation}>
                    <IconHeartFilled /> Donate
                  </Button>
                  <Button
                    className="sd-btn sd-btn--outline"
                    onClick={() => navigate("/interns")}
                  >
                    <IconUserPlus /> Join Us
                  </Button>
                </div>
              </div>
              <div className="sd-cta-image">
                <img
                  src="/assets/images/backgrounds/do4.png"
                  alt="Hands together for social inclusion"
                />
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default SocialDevelopment;